import React, { useEffect, useRef, useState, useCallback } from 'react'
import { api } from 'core/api/ConnectionManager'

type WindowStyles = {
    width?: string | number
    height?: string | number
    left?: string | number
    right?: string | number
    top?: string | number
    borderRadius?: string | number
    transform?: string
}

export const DeviceTerminalMethods = ({ device }: any) => {
    const [sendInProgress, setSendInProgress] = useState(false)
    const [directory, setDirectory] = useState<string>('')
    const [terminalLogs, setTerminalLogs] = useState<string[]>([])
    const [inputString, setInputString] = useState<string>('')
    const [windowStyles, setWindowStyles] = useState<WindowStyles | {}>({})
    const [minimize, setMinimize] = useState<boolean>(false)
    const [maximize, setMaximize] = useState<boolean>(false)

    const monitorRef = useRef<HTMLDivElement>(null)
    const resizerLeftRef = useRef<HTMLDivElement>(null)
    const resizerRightRef = useRef<HTMLDivElement>(null)
    const resizerBottomRef = useRef<HTMLDivElement>(null)
    const resizerTopRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<any>(null)
    const savedStylesRef = useRef<WindowStyles | null>(null)

    const getInitialStyles = useCallback((): WindowStyles => {
        const screenWidth = window.innerWidth
        const screenHeight = window.innerHeight
        const monitorWidth = screenWidth / 2.4
        const monitorHeight = screenHeight / 2.4
        const left = (screenWidth - monitorWidth) / 2
        const top = (screenHeight - monitorHeight) / 2
        const mediaQuery = window.matchMedia('(max-width: 500px)').matches

        return {
            width: mediaQuery ? '90%' : monitorWidth + 'px',
            height: monitorHeight + 'px',
            left: mediaQuery ? '50%' : left + 'px',
            top: top + 'px',
            borderRadius: 10,
            transform: mediaQuery ? 'translateX(-50%)' : 'none',
        }
    }, [])

    // Initialize on mount
    useEffect(() => {
        setWindowStyles(getInitialStyles())
    }, [getInitialStyles])

    // Handle minimize
    useEffect(() => {
        if (minimize) {
            // Save current state before minimize
            setWindowStyles((current) => {
                savedStylesRef.current = current
                return {
                    ...current,
                    width: '400px',
                    height: '64px',
                }
            })
        } else {
            // Restore saved state or initial
            setWindowStyles(savedStylesRef.current || getInitialStyles())
            savedStylesRef.current = null
        }
    }, [minimize, getInitialStyles])

    // Handle maximize
    useEffect(() => {
        if (maximize) {
            // Save current state before maximize
            setWindowStyles((current) => {
                savedStylesRef.current = current
                return {
                    width: '100%',
                    height: '100%',
                    top: 0,
                    left: 0,
                    borderRadius: 0,
                }
            })
        } else {
            // Restore saved state or initial
            setWindowStyles(savedStylesRef.current || getInitialStyles())
            savedStylesRef.current = null
        }
    }, [maximize, getInitialStyles])

    const onMinimize = useCallback(() => {
        setMinimize((prev) => !prev)
    }, [])

    const onMaximize = useCallback(() => {
        setMaximize((prev) => !prev)
    }, [])

    const onDragStart = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
    }, [])

    const onMouseDown = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (maximize) return

            const target = e.target as HTMLElement
            if (
                target === resizerRightRef.current ||
                target === resizerLeftRef.current ||
                target === resizerTopRef.current ||
                target === resizerBottomRef.current
            ) {
                return
            }

            const monitorElement = monitorRef.current
            if (!monitorElement) return

            const rect = monitorElement.getBoundingClientRect()
            const shiftX = e.clientX - rect.left
            const shiftY = e.clientY - rect.top
            const windowWidth = window.innerWidth
            const windowHeight = window.innerHeight

            // Save initial element position
            const initialTop = parseFloat(((windowStyles as any).top as string)?.replace('px', '') || '0')
            const initialLeft = parseFloat(((windowStyles as any).left as string)?.replace('px', '') || '0')

            const handleMouseMove = (moveEvent: MouseEvent) => {
                const deltaX = moveEvent.clientX - (rect.left + shiftX)
                const deltaY = moveEvent.clientY - (rect.top + shiftY)

                const newTop = Math.max(0, Math.min(initialTop + deltaY, windowHeight - 64))
                const newLeft = Math.max(
                    -monitorElement.offsetWidth + 64,
                    Math.min(initialLeft + deltaX, windowWidth - 64)
                )

                setWindowStyles((prev) => ({
                    ...prev,
                    top: newTop + 'px',
                    left: newLeft + 'px',
                    right: undefined,
                }))
            }

            const handleMouseUp = () => {
                document.removeEventListener('mousemove', handleMouseMove)
                document.removeEventListener('mouseup', handleMouseUp)
            }

            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
        },
        [maximize, windowStyles]
    )

    // Touch support for dragging
    const onTouchStart = useCallback(
        (e: React.TouchEvent<HTMLDivElement>) => {
            if (maximize || e.touches.length !== 1) return

            const target = e.target as HTMLElement
            if (
                target === resizerRightRef.current ||
                target === resizerLeftRef.current ||
                target === resizerTopRef.current ||
                target === resizerBottomRef.current
            ) {
                return
            }

            const monitorElement = monitorRef.current
            if (!monitorElement) return

            const touch = e.touches[0]
            const windowWidth = window.innerWidth
            const windowHeight = window.innerHeight

            // Save initial element position and touch point
            const initialTop = parseFloat(((windowStyles as any).top as string)?.replace('px', '') || '0')
            const initialLeft = parseFloat(((windowStyles as any).left as string)?.replace('px', '') || '0')
            const initialTouchX = touch.clientX
            const initialTouchY = touch.clientY

            const handleTouchMove = (moveEvent: TouchEvent) => {
                if (moveEvent.touches.length !== 1) return

                const touch = moveEvent.touches[0]
                const deltaX = touch.clientX - initialTouchX
                const deltaY = touch.clientY - initialTouchY

                const newTop = Math.max(0, Math.min(initialTop + deltaY, windowHeight - 64))
                const newLeft = Math.max(
                    -monitorElement.offsetWidth + 64,
                    Math.min(initialLeft + deltaX, windowWidth - 64)
                )

                setWindowStyles((prev) => ({
                    ...prev,
                    top: newTop + 'px',
                    left: newLeft + 'px',
                    right: undefined,
                }))
            }

            const handleTouchEnd = () => {
                document.removeEventListener('touchmove', handleTouchMove)
                document.removeEventListener('touchend', handleTouchEnd)
            }

            document.addEventListener('touchmove', handleTouchMove, { passive: false })
            document.addEventListener('touchend', handleTouchEnd)
        },
        [maximize, windowStyles]
    )

    // Resizers
    useEffect(() => {
        if (minimize || maximize) return

        const resizeableEle = monitorRef.current
        if (!resizeableEle) return

        const createResizeHandler = (direction: 'right' | 'left' | 'top' | 'bottom') => {
            return (startEvent: MouseEvent) => {
                const startX = startEvent.clientX
                const startY = startEvent.clientY
                const startWidth = resizeableEle.offsetWidth
                const startHeight = resizeableEle.offsetHeight
                const startLeft = resizeableEle.offsetLeft

                const handleMouseMove = (moveEvent: MouseEvent) => {
                    const deltaX = moveEvent.clientX - startX
                    const deltaY = moveEvent.clientY - startY

                    setWindowStyles((prev) => {
                        const updates: WindowStyles = { ...prev }

                        if (direction === 'right') {
                            updates.width = Math.max(300, startWidth + deltaX) + 'px'
                        } else if (direction === 'left') {
                            const newWidth = Math.max(300, startWidth - deltaX)
                            updates.width = newWidth + 'px'
                            updates.left = startLeft + deltaX + 'px'
                            updates.right = undefined
                        } else if (direction === 'bottom') {
                            updates.height = Math.max(64, startHeight + deltaY) + 'px'
                        } else if (direction === 'top') {
                            const newHeight = Math.max(64, startHeight - deltaY)
                            updates.height = newHeight + 'px'
                            updates.top = resizeableEle.offsetTop + deltaY + 'px'
                        }

                        return updates
                    })
                }

                const handleMouseUp = () => {
                    document.removeEventListener('mousemove', handleMouseMove)
                    document.removeEventListener('mouseup', handleMouseUp)
                }

                document.addEventListener('mousemove', handleMouseMove)
                document.addEventListener('mouseup', handleMouseUp)
            }
        }

        const resizerRight = resizerRightRef.current
        const resizerLeft = resizerLeftRef.current
        const resizerTop = resizerTopRef.current
        const resizerBottom = resizerBottomRef.current

        resizerRight?.addEventListener('mousedown', createResizeHandler('right'))
        resizerLeft?.addEventListener('mousedown', createResizeHandler('left'))
        resizerTop?.addEventListener('mousedown', createResizeHandler('top'))
        resizerBottom?.addEventListener('mousedown', createResizeHandler('bottom'))

        return () => {
            resizerRight?.removeEventListener('mousedown', createResizeHandler('right'))
            resizerLeft?.removeEventListener('mousedown', createResizeHandler('left'))
            resizerTop?.removeEventListener('mousedown', createResizeHandler('top'))
            resizerBottom?.removeEventListener('mousedown', createResizeHandler('bottom'))
        }
    }, [minimize, maximize])

    const onSend = useCallback(() => {
        if (inputString.trim()) {
            setTerminalLogs((prev) => [...prev, inputString])
        }

        setSendInProgress(true)

        api.send(
            'deviceTerminalSendCommand',
            { digitalSignageId: device.id, command: inputString },
            { hideLoader: true }
        )

        setInputString('')
    }, [inputString, device.id])

    const onCancel = useCallback(() => {
        api.send('deviceTerminalCancelCommand', { digitalSignageId: device.id }, { hideLoader: true })
        setSendInProgress(false)
    }, [device.id])

    // Initial send on mount - ONLY ONCE
    useEffect(() => {
        const timer = setTimeout(() => {
            setSendInProgress(true)
            api.send('deviceTerminalSendCommand', { digitalSignageId: device.id, command: '' }, { hideLoader: true })
        }, 0)

        return () => clearTimeout(timer)
    }, [device.id])

    // Listen for messages
    useEffect(() => {
        const listenersId: string[] = []

        const onReceiveLog = (dataLog: any) => {
            if (dataLog.digitalSignageId === device.id) {
                if (!dataLog.isRunningCommand) {
                    setSendInProgress(false)
                }

                if (dataLog.message) {
                    setTerminalLogs((prev) => [...prev, dataLog.message])

                    if (dataLog.directory) {
                        setDirectory(dataLog.directory)
                    }
                }
            }
        }

        api.addObserver('deviceTerminalMessageReceived', onReceiveLog, listenersId)

        return () => listenersId.forEach((id) => api.removeObserver(id))
    }, [device.id])

    return {
        onMinimize,
        onMaximize,
        windowStyles,
        minimize,
        maximize,
        onDragStart,
        onMouseDown,
        onTouchStart,
        monitorRef,
        resizerLeftRef,
        resizerRightRef,
        resizerTopRef,
        resizerBottomRef,
        contentRef,
        onSend,
        inputString,
        setInputString,
        terminalLogs,
        directory,
        sendInProgress,
        onCancel,
    }
}
